import type { CommandeCalculee, EtapeCochable, Reglages } from '@mosaik/core';

/**
 * Les types viennent de `@mosaik/core`, le même paquet qu'importe l'API : le
 * front et le serveur ne peuvent pas diverger sur la forme du calcul.
 */
export type {
  ActionCalculee, CleLitige, CommandeCalculee, ContactFournisseur, Etape, Gravite, LangueRelance,
  Litige, ModeleMail, Reglages,
} from '@mosaik/core';

export interface LiensCommande {
  suivi: string | null;
  suiviIndisponible: string | null;
  hubspotContactId: string | null;
  /** Lien direct vers la fiche HubSpot, dès que l'identifiant a été résolu une fois. */
  hubspot: string | null;
  /** Commande dans son outil d'origine, Shopify ou Sellsy. `null` sans identifiant. */
  boutique: string | null;
  boutiqueOrigine: 'shopify' | 'sellsy' | null;
  /** La commande sur le portail Fortemix, `null` tant qu'un relevé ne l'y a pas trouvée. */
  portailFortemix: string | null;
}

/** Les trois envois automatiques, chacun avec son propre état. */
export type CanalEnvoi = 'fournisseur' | 'expedition' | 'tracking';

export interface EtatEnvoiCommande {
  /**
   * `ignore` est une DÉCISION — on a jugé ce mail inutile —, là où `desactive`
   * est un empêchement : Outlook était injoignable. Les confondre ferait
   * disparaître une tâche pour une panne, ou ressortir un mail qu'on a écarté.
   */
  etat: 'non_requis' | 'a_envoyer' | 'envoye' | 'echec' | 'desactive' | 'ignore';
  tentatives: number;
  derniereErreur: string | null;
  /**
   * Mails enregistrés au dernier envoi. `0` sur une commande antérieure à
   * leur enregistrement : le mail a été préparé, mais ne peut pas être relu.
   */
  mails: number;
  /** Date du dernier envoi enregistré, ISO (nom historique : `deposeLe`). */
  deposeLe: string | null;
}

/** Un mail d'envoi, relu dans Outlook quand c'est possible. */
export interface MailEnvoiLu {
  messageId: string | null;
  objet: string | null;
  destinataires: string[];
  societe: string | null;
  deposeLe: string | null;
  lienWeb: string | null;
  simule: boolean;
  /** Le message tel qu'il est dans la boîte, `null` s'il n'a pas pu être relu. */
  message: {
    id: string;
    objet: string;
    destinataires: string[];
    copie: string[];
    corpsHtml: string;
    /** `true` si le message est resté brouillon — l'envoi n'a pas abouti, ou mail d'avant 2026-10-02. */
    brouillon: boolean;
    envoyeLe: string | null;
    creeLe: string | null;
    lienWeb: string | null;
  } | null;
  /** Pourquoi `message` est `null`. */
  raison: string | null;
}

export const LIBELLE_CANAL: Record<CanalEnvoi, string> = {
  fournisseur: 'Commande au fournisseur',
  expedition: "Mail d'expédition",
  tracking: 'Mail de tracking',
};

/**
 * Un colis de plus, quand la commande part en plusieurs envois. Le colis
 * principal reste `tracking` : c'est lui que suivent l'étape et le relevé.
 */
export interface ColisSupplementaire {
  tracking: string;
  transporteur: string | null;
  suivi: string | null;
  ajouteLe: string | null;
  /** Son propre relevé : sa livraison ne clôt pas la commande. */
  statutTransporteur: string | null;
  dateStatutTransporteur: string | null;
  livre: boolean;
  releveLe: string | null;
  releveErreur: string | null;
  /** Le mail de ce colis au client : à envoyer, parti, écarté, ou en échec. */
  mailEtat: 'a_envoyer' | 'envoye' | 'ignore' | 'echec';
  mailErreur: string | null;
  /** Mails de tracking partis pour ce colis, le plus récent en dernier. */
  mails: Array<{ envoyeLe: string | null; destinataires: string[]; lienWeb: string | null; simule: boolean }>;
}

export interface CommandeListee {
  id: string;
  ref: string;
  /** Jour civil. `null` si la date en base est illisible — on ne l'invente pas. */
  dateReception: string | null;
  client: string;
  entreprise: string | null;
  email: string | null;
  telephone: string | null;
  langue: string | null;
  ville: string | null;
  pays: string | null;
  /** Adresse de livraison complète, présente dès la liste : le mail fournisseur en a besoin. */
  adresse: {
    nom: string | null; telephone: string | null; ligne: string | null; complement: string | null;
    ville: string | null;
    codePostal: string | null;
    pays: string | null; detailsLivraison: string | null;
  };
  /** Pourquoi l'adresse ne se corrige plus — enlèvement demandé, expédiée —, `null` sinon. */
  adresseVerrouillee: string | null;
  montant: number;
  montantLivraison: number | null;
  fournisseurs: string[];
  referencesIndeterminees: string[];
  /**
   * Lignes produit À COMMANDER, avec leur désignation issue du référentiel.
   * Les articles remboursés dans Shopify n'y figurent pas.
   */
  articles: ArticleCommande[];
  /** Ce que Shopify a retiré de la commande (remboursements, annulation). */
  articlesRetires: { reference: string | null; quantite: number }[];
  /** Ce que Shopify a changé après la création, `null` hors Shopify. */
  shopify: {
    annuleeLe: string | null;
    motifAnnulation: string | null;
    statutFinancier: string | null;
    /** Changement survenu après transmission, que le fournisseur ignore encore. */
    aSignaler: string | null;
    /** Identifiants des changements connus, renvoyés à l'envoi au fournisseur. */
    ids: string[];
    /** Annulée, ou plus rien à livrer : la commande est close. */
    close: boolean;
  } | null;
  dateEnvoiFournisseur: string | null;
  /** Dernière relance réellement partie, `null` s'il n'y en a pas. */
  dateRelanceFournisseur: string | null;
  relancesFournisseur: number;
  relancesTransporteur: number;
  /** Relances parties par mail, la plus récente en tête, avec leur lien Outlook. */
  relancesEnvoyees: Array<{
    le: string | null;
    cible: 'fournisseur' | 'transporteur';
    destinataires: string | null;
    lienWeb: string | null;
  }>;
  /** Nombre de problèmes connus : relances parties, incidents signalés. */
  problemesConnus: number;
  dateExpedition: string | null;
  /** Jour jusqu'auquel la commande patiente, `null` si elle n'attend rien. */
  attenteJusquAu: string | null;
  /** Ce qu'on attend, en toutes lettres. */
  attenteMotif: string | null;
  incident: string | null;
  transporteur: string | null;
  tracking: string | null;
  /** Colis partis en plus du principal, chacun avec son lien et ses mails. */
  colis: ColisSupplementaire[];
  /** Premier colis livré, d'autres encore en route : la commande les attend. */
  premierColisLivre: boolean;
  /** Le colis que la liste montre : celui au plus mal, premier compris. */
  suiviAffiche: {
    tracking: string; transporteur: string | null; statut: string | null; jour: string | null;
    premier: boolean; nombreColis: number;
  } | null;
  statutTransporteur: string | null;
  dateStatutTransporteur: string | null;
  /** Dernier relevé auprès du transporteur — à ne pas confondre avec la date de l'événement. */
  releveLe: string | null;
  releveErreur: string | null;
  livre: boolean;
  avisDemande: boolean;
  /** La demande d'avis : la case, son échéance, son dernier appel. */
  avis: {
    demande: boolean;
    envoye: boolean;
    programmePour: string | null;
    /** Jour de l'appel abouti, distinct de l'échéance quand l'envoi fut immédiat. */
    envoyeLe: string | null;
    automatique: boolean;
    erreur: string | null;
    /** Cocher enverrait la demande immédiatement : l'échéance est déjà passée. */
    partiraMaintenant: boolean;
    /** La case s'offre encore : livrée, rien de programmé, fenêtre ouverte. */
    cochable: boolean;
    /** Le numéro qui partira, et pourquoi il ne part pas s'il ne le peut pas. */
    telephone: {
      /** Le numéro enregistré, tel quel. `null` si la commande n'en porte pas. */
      brut: string | null;
      /** Sa forme internationale, `null` s'il n'est pas exploitable. */
      e164: string | null;
      valide: boolean;
      raison: string | null;
    };
  };
  note: string | null;
  envois: Record<CanalEnvoi, EtatEnvoiCommande>;
  liens: LiensCommande;
  /** Règlement de la facture chez Sellsy, `null` tant qu'il n'a pas été relevé. */
  paiement: {
    statut: string;
    regle: boolean;
    releveLe: string;
    resteDu: number | null;
  } | null;
  /** Les cases de la fiche, coches manuelles comprises. */
  etapes: Record<EtapeCochable, {
    fait: boolean;
    /** Ce que disent les données, sans la coche manuelle. */
    automatique: boolean;
    /** La coche posée à la main, `null` si la case suit les données. */
    manuel: { valeur: boolean; le: string; par: string | null } | null;
  }>;
  /** L'enlèvement DHL réservé, `null` s'il n'y en a pas (le bac à sable n'y figure jamais). */
  enlevement: {
    /** `null` quand la demande est partie sans réponse certaine. */
    envoiId: string | null;
    jourEnlevement: string | null;
    reserveLe: string;
    transport: 'dhl' | 'dpd';
    /** Demande partie, issue inconnue : à vérifier sur le portail du transporteur. */
    incertain: boolean;
  } | null;
  /** Attente du règlement choisie, `null` si la commande ne l'attend pas. */
  attenteReglement: { depuis: string; par: string | null } | null;
  /** Relances de règlement envoyées au client. */
  relanceReglement: {
    nombre: number;
    /** Date de la dernière, ISO. */
    derniereLe: string | null;
  };
  calcul: CommandeCalculee;
}

export interface ArticleCommande {
  reference: string | null;
  /** Désignation du référentiel, `null` si la référence n'y figure pas. */
  nom: string | null;
  /** Désignation du fournisseur : c'est elle qui part dans les mails qu'il reçoit. */
  correspondance: string | null;
  /** Unité de vente du référentiel (`unité`, `m2`). */
  unite: string | null;
  quantite: number | null;
  fournisseur: string | null;
}

export interface EntreeHistorique {
  at: string;
  texte: string;
  auteur: string;
  resultat: 'info' | 'ok' | 'echec';
}

export interface CommandeDetaillee extends CommandeListee {
  histo: EntreeHistorique[];
  /** Note de l'équipe, signée et datée. `null` tant que personne n'en a écrit. */
  noteUtilisateur: { texte: string; par: string; le: string } | null;
  /** Provenance : fichier d'import et numéro de ligne. */
  source: { fichier: string | null; ligne: number | null; importeLe: string | null };
  creeeLe: string | null;
  modifieeLe: string | null;
}

export interface PageCommandes {
  elements: CommandeListee[];
  total: number;
  page: number;
  taille: number;
  pages: number;
  compteurs: Record<string, number>;
}

export interface EntreeJournal {
  id: string;
  at: string;
  ref: string | null;
  evenement: string;
  detail: string;
  resultat: 'ok' | 'echec';
  erreur: string | null;
  simule: boolean;
  auteur: string;
}

/** Ce que devient le mail d'une relance (`depose` : nom historique de « envoyé »). */
export interface EtatBrouillonRelance {
  depose: boolean;
  simule: boolean;
  erreur: string | null;
  lienWeb: string | null;
}

/**
 * Réponse de `POST /commandes/:id/relance`.
 *
 * La commande y est comme après toute mutation ; `brouillon` dit séparément ce
 * qu'est devenu le mail. Une relance dont l'envoi a échoué n'est pas
 * enregistrée : l'action reste due.
 */
export interface ResultatRelance {
  commande: CommandeDetaillee;
  /** La relance n'est acquise que si `depose` vaut `true`. */
  brouillon: EtatBrouillonRelance;
}

/** Ce qu'est devenu le mail de commande d'UN fournisseur. */
export interface BrouillonFournisseur {
  societe: string;
  destinataires: string[];
  articles: number;
  depose: boolean;
  simule: boolean;
  erreur: string | null;
  lienWeb: string | null;
}

/**
 * Réponse de `POST /commandes/:id/fournisseur`.
 *
 * La commande n'est transmise que si tous les mails sont partis ;
 * `brouillons` permet de nommer celui qui manque.
 */
export interface ResultatCommandeFournisseur {
  commande: CommandeDetaillee;
  brouillons: BrouillonFournisseur[];
  /** Références sans fournisseur connu : aucune commande n'a pu être préparée. */
  referencesIgnorees: string[];
}

/** Une référence du référentiel produits, telle que la recherche la renvoie. */
export interface ProduitReferentiel {
  reference: string;
  nom: string;
  correspondance: string | null;
  unite: string | null;
  fournisseur: string | null;
  actif: boolean;
}

/** Ce que le référentiel porte réellement, pour distinguer deux causes voisines. */
export interface EtatReferentiel {
  references: number;
  correspondances: number;
  prefixes: number;
}

export interface PageReferentiel {
  elements: ProduitReferentiel[];
  total: number;
  page: number;
  taille: number;
  pages: number;
}

export interface PageJournal {
  elements: EntreeJournal[];
  total: number;
  page: number;
  taille: number;
  pages: number;
}

export interface DocumentReglages extends Reglages {
  modifiePar: string | null;
  updatedAt: string;
}

export interface EtatIntegration {
  disponible: boolean;
  simule: boolean;
  raison: string | null;
}

export interface Sante {
  statut: 'ok' | 'degrade' | 'hs';
  version: string;
  environnement: string;
  base: { etat: 'ok' | 'reveil' | 'injoignable'; latenceMs: number | null; message: string };
  /** Mails composés par l'application : relus à l'écran, envoyés au clic depuis cette boîte. */
  brouillons: { boite: string; boiteRefusee: string | null; regle: string };
  /**
   * Relevé du suivi transporteur : qui est relevable, et sinon pourquoi.
   *
   * L'écran annonçait un relevé « à 07:00 et 19:00 » qui n'existait pas. Il
   * existe désormais, mais tous les transporteurs n'y ont pas droit — d'où la
   * raison, transporteur par transporteur.
   */
  suivi: {
    automatique: boolean;
    heures: number[];
    transporteurs: Record<string, EtatIntegration>;
  };
  integrations: {
    /** Envoi des mails depuis Outlook. */
    outlook: EtatIntegration;
    /** État de chacun des trois canaux : tous passent par Outlook. */
    canaux: Record<CanalEnvoi, EtatIntegration>;
    hubspot: EtatIntegration;
    /** Portail fournisseur Fortemix : dates d'expédition et trackings relevés. */
    fortemix: EtatIntegration & { automatique: boolean };
  };
}

export interface ContactHubspot {
  id: string;
  email: string;
  prenom: string | null;
  nom: string | null;
  url: string;
}

export type ResolutionHubspot =
  | { statut: 'trouve'; contact: ContactHubspot; simule: boolean }
  | { statut: 'doublons'; contacts: ContactHubspot[]; simule: boolean }
  | { statut: 'absent'; email: string; urlCreation: string | null; simule: boolean }
  | { statut: 'sans_email'; simule: boolean }
  | { statut: 'indisponible'; raison: string; simule: boolean };

export interface Identite {
  id: string;
  email: string;
  nom: string;
  motDePasseAChanger?: boolean;
}
